import { cssColor } from './settingValue.js';
/** One setting off an untyped, untrusted `section.settings` — `undefined` for anything that is not an object. */
function read(s, key) {
    return s && typeof s === 'object' ? s[key] : undefined;
}
/** Seller-facing group the four knobs live in. */
export const TEXT_COLORS_GROUP = 'Text colors';
/**
 * The four inspector fields — spread into a schema's `fields[]`, like `animationFields`.
 *
 * `group` is overridable for the sections that name their groups differently, but the default is what the
 * sweep uses so the group reads the same (and translates once) across the whole theme.
 */
export function textColorFields(group = TEXT_COLORS_GROUP) {
    return [
        {
            id: 'headingColor',
            type: 'color',
            label: 'Heading color',
            group,
            auto: true,
            info: 'Applies to this section’s heading and to the heading of each card inside it. Left automatic, they follow the color scheme.',
        },
        {
            id: 'headingGradient',
            type: 'gradient',
            label: 'Heading gradient',
            group,
            auto: true,
            info: 'Paints the headings with a gradient instead of a flat color. Overrides the heading color while it is set.',
        },
        {
            id: 'textColor',
            type: 'color',
            label: 'Text color',
            group,
            auto: true,
            info: 'Applies to this section’s body text — its description and each card’s text.',
        },
        {
            id: 'textGradient',
            type: 'gradient',
            label: 'Text gradient',
            group,
            auto: true,
            info: 'Paints the body text with a gradient instead of a flat color. Overrides the text color while it is set.',
        },
    ];
}
/** The two settings one role reads. */
const KEYS = {
    heading: { color: 'headingColor', gradient: 'headingGradient' },
    text: { color: 'textColor', gradient: 'textGradient' },
};
/**
 * A gradient string that is SAFE TO PAINT.
 *
 * Same threat as {@link cssColor}: a stored document is JSON, so the value that comes back can be any
 * string, and it is about to be interpolated into a style. Inline styles cannot close a rule the way a
 * scoped `<style>` can, but a `url(...)` here would still fetch, so we accept only what the gradient
 * control itself writes: a `linear-gradient(...)` / `radial-gradient(...)` / `conic-gradient(...)` (or
 * their `repeating-` forms) made of colours, numbers, units and separators — nothing else.
 */
const SAFE_GRADIENT = /^(repeating-)?(linear|radial|conic)-gradient\([#a-z0-9(),.%\s/-]*\)$/i;
export function safeGradient(v) {
    if (typeof v !== 'string')
        return undefined;
    const g = v.trim();
    if (!g || !SAFE_GRADIENT.test(g))
        return undefined;
    // A `url(` can only arrive through the character class above as part of a colour function name; reject
    // the whole value if the word appears at all rather than reason about where it sits.
    return /url\s*\(/i.test(g) ? undefined : g;
}
/**
 * The same paint as CSS declarations, for the sections that colour their words from a scoped `<style>`
 * instead of an inline style (a rule that has to reach `.cls__title` inside a slider track, say). Returns
 * `''` when untouched. The caller supplies the selector and the braces:
 *
 *   `.${cls}__title{${textPaintCss(s, 'heading')}}`
 */
export function textPaintCss(s, role) {
    const k = KEYS[role];
    const gradient = safeGradient(read(s, k.gradient));
    if (gradient) {
        // No `color:transparent`. `-webkit-text-fill-color` wins over `color` everywhere it is supported
        // (every current engine, Firefox included), and where it is NOT, leaving `color` alone degrades to
        // readable flat text instead of a heading nobody can see.
        return `background-image:${gradient};-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent`;
    }
    const raw = read(s, k.color);
    if (raw === undefined || raw === null)
        return '';
    const color = cssColor(raw, '');
    return color ? `color:${color}` : '';
}
/** Every selector in one `:is()`, so the rule stays one line and its specificity stays flat. */
const HEADING_ELEMENTS = ':is(h1,h2,h3,h4,h5,h6)';
/**
 * The body-text carriers. Deliberately NOT `*` and NOT `.rte *`: a `*` under a richtext container also
 * matches the `<h3>` a seller typed into it, and it would win the cascade over the heading rule below
 * (a class-anchored descendant beats a bare element). Listing the block elements a paragraph of copy is
 * actually made of keeps the two roles disjoint, which is what lets a section carry both at once.
 */
const TEXT_ELEMENTS = ':is(p,li,blockquote,figcaption,dd,dt)';
/** `a;b` → `a!important;b!important` — an override the seller asked for has to beat an inline style. */
function important(decls) {
    return decls
        .split(';')
        .filter((d) => d.length > 0)
        .map((d) => `${d}!important`)
        .join(';');
}
/**
 * The whole section's text paint, for its scoped `<style>`. `''` when the seller has touched nothing, so
 * an untouched section emits not one extra byte.
 *
 * `sel` is the section's own root selector (`.${cls}`), which is what keeps these rules inside this band —
 * two multicolumns on one page each paint their own words.
 */
export function textPaintSectionCss(sel, s) {
    const heading = textPaintCss(s, 'heading');
    const text = textPaintCss(s, 'text');
    let css = '';
    if (text) {
        // Flat colour only on the root: a gradient there would clip the BAND's own background to its text.
        const flat = safeGradient(read(s, 'textGradient')) ? '' : text;
        if (flat)
            css += `${sel}{${important(flat)}}`;
        css += `${sel} ${TEXT_ELEMENTS}{${important(text)}}`;
    }
    // Last, so a heading that sits inside a body-text container still reads as a heading.
    if (heading)
        css += `${sel} ${HEADING_ELEMENTS}{${important(heading)}}`;
    return css;
}
// ── The BLOCK side ────────────────────────────────────────────────────────────────────────────────
//
// A section's rule reaches every heading in its band, which is the right tool for "make this whole
// section's titles blue". It is the wrong tool for the Rich text section, where the heading IS a block:
// the seller clicks the word "Heading" in the rail, gets the BLOCK inspector, and the section's controls
// are not there (founder, 2026-09-20: "I don't see it in the heading of a rich text component").
//
// So a text block that already offers its own custom colour gets the gradient beside it, in the same
// group, behind the same switch. The block wins over its section when both are set — `-webkit-text-fill-
// color` beats `color` whatever its priority, and the narrower control should win anyway.
/**
 * The gradient knob for a text BLOCK, next to that block's own colour field.
 *
 * `enabledBy` is the id of the checkbox the block already gates its colour on (`enableCustomColor`
 * everywhere today), so the pair appears and disappears together — a gradient revealed on its own, next
 * to a hidden colour, reads as two unrelated controls.
 */
export function blockGradientField(group, enabledBy = 'enableCustomColor') {
    return {
        id: 'customGradient',
        type: 'gradient',
        label: 'Text gradient',
        group,
        auto: true,
        info: 'Paints the text with a gradient instead of a flat color. Overrides the color above while it is set.',
        visibleWhen: { field: enabledBy, equals: true },
    };
}
/**
 * The rule that paints one block's text with its gradient — `''` when unset, or when the switch above it
 * is off (a gradient left behind by a seller who unticked the box must not keep painting).
 *
 * The block keeps its inline `color`: `-webkit-text-fill-color` wins over it wherever it is supported,
 * and where it is not, that colour is what keeps the text readable instead of invisible.
 */
export function blockGradientCss(sel, s, enabledBy = 'enableCustomColor') {
    if (read(s, enabledBy) !== true)
        return '';
    const gradient = safeGradient(read(s, 'customGradient'));
    return gradient
        ? `${sel}{background-image:${gradient};-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent}`
        : '';
}
//# sourceMappingURL=textColor.js.map